"use client"

import { useState, useRef, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Send, X, User, Loader2, BarChart4 } from "lucide-react";
import { Avatar, Button, ScrollArea } from "@mantis-sas/ui";
import { analyzeFlowStats } from "../../../../ApiCalls/mantisStats"
import { getStatsPayload } from "../../../../utils/payloadStats"
import {connect} from "react-redux";
import { selectAuthState } from "../../../../store/selectors/authSelectors"

function FlowCreateStats({ flowData, isOpen, onToggle, mantisBusiness, token, user }) {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hola, soy Mantis tu asistente de análisis estadístico. Puedo ayudarte a analizar las métricas y tendencias de este flujo. ¿Qué te gustaría saber?",
      timestamp: new Date()
    }
  ])
  const [input, setInput] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [statsData, setStatsData] = useState({
    currentStats: [],
    columnsExample: {}
  })
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)

  // Cargar datos de estadísticas cuando se abre el componente
  useEffect(() => {
    const loadStatsData = async () => {
      if (flowData && flowData.id) {
        const clientId = mantisBusiness._id;
        const workflowId = flowData.id;
        
        try {
          const payload = await getStatsPayload(clientId, workflowId, user._id);
          if (payload.success) {
            setStatsData({
              currentStats: payload.currentStats,
              columnsExample: payload.columnsExample
            });
          } else {
            console.error("Error al cargar datos de estadísticas:", payload.error);
          }
        } catch (error) {
          console.error("Error al preparar datos para análisis:", error);
        }
      }
    };
    
    if (isOpen) {
      loadStatsData();
    }
  }, [isOpen, flowData, mantisBusiness._id, user._id]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages])

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus()
      }, 300)
    }
  }, [isOpen])

  const handleSendMessage = async () => {
    if (!input.trim()) return

    // Add user message
    const userMessage = {
      role: "user",
      content: input,
      timestamp: new Date()
    }
    
    setMessages(prev => [...prev, userMessage])
    setInput("")
    setIsLoading(true)

    try {
      // Llamada a la API de análisis de estadísticas
      const statsResponse = await analyzeFlowStats(
        input, // prompt del usuario
        statsData.currentStats, // estadísticas actuales
        statsData.columnsExample, // ejemplo de columnas
        mantisBusiness._id, // ID del cliente
        flowData.id // ID del flujo
      )
      
      console.log("Respuesta de análisis:", statsResponse)
      let responseContent = ""
      
      if (statsResponse.success) {
        // Extraer el mensaje del objeto de respuesta
        if (statsResponse.data && statsResponse.data.message) {
          responseContent = statsResponse.data.message;
          
          // Si se actualizaron las estadísticas, recargar los datos
          if (statsResponse.data.query && statsResponse.data.query.trim() !== '') {
            // Esperar un momento para que la BD se actualice
            setTimeout(async () => {
              const payload = await getStatsPayload(mantisBusiness._id, flowData.id, user._id);
              if (payload.success) {
                setStatsData({
                  currentStats: payload.currentStats,
                  columnsExample: payload.columnsExample
                });
              }
            }, 1000);
          }
        } else if (statsResponse.data && statsResponse.data.response) {
          responseContent = statsResponse.data.response;
        } else {
          responseContent = "He analizado las estadísticas según tu solicitud.";
        }
      } else {
        throw new Error(statsResponse.error || "Error al analizar las estadísticas")
      }    

      const aiMessage = {
        role: "assistant",
        content: responseContent,
        timestamp: new Date()
      }
      
      setMessages(prev => [...prev, aiMessage])
    } catch (error) {
      console.error("Error al procesar la consulta:", error)
      
      // Add error message
      const errorMessage = {
        role: "assistant",
        content: "Lo siento, hubo un error al analizar las estadísticas. Por favor, intenta de nuevo más tarde.",
        timestamp: new Date(),
        isError: true
      }
      
      setMessages(prev => [...prev, errorMessage])
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  // Función para renderizar el contenido con formato
  const renderFormattedContent = (content) => {
    // Dividir el contenido por líneas
    const lines = content.split('\n');
    
    return (
      <>
        {lines.map((line, index) => (
          <div key={index}>
            {line}
            {index < lines.length - 1 && <br />}
          </div>
        ))}
      </>
    );
  };

  return (
    <div className="fixed right-0 bottom-0 z-[1000] flex items-end">
      {/* Chat panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: 400, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 400, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="h-[calc(100vh-100px)] w-[400px] bg-white dark:bg-slate-800 shadow-xl border-l border-slate-200 dark:border-slate-700 flex flex-col rounded-l-2xl overflow-hidden mb-20 mr-5"
          >
            {/* Header */}
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between bg-gradient-to-r from-purple-50 to-white dark:from-purple-900/30 dark:to-slate-800">
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10 shadow-md flex items-center justify-center bg-gradient-to-br from-purple-500 to-purple-600">
                  <img 
                    src="/mantisWhiteIsotype.png" 
                    alt="Mantis Logo" 
                    className="h-6 w-6 object-contain"
                  />
                </Avatar>
                <div>
                  <h3 className="font-medium text-slate-800 dark:text-slate-200">
                    Asistente de Estadísticas
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Analizando estadísticas de {flowData?.name || 'flujo actual'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={onToggle} 
                  className="rounded-full hover:bg-purple-100 dark:hover:bg-slate-600 transition-all duration-200"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            {/* Messages */}
            <ScrollArea className="flex-1">
              <div className="p-4 space-y-4">
                {messages.map((message, index) => (
                  <div key={index} className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`flex gap-3 max-w-[85%] ${message.role === "user" ? "flex-row-reverse" : ""}`}>
                      {message.role === "user" ? (
                        <div className="h-8 w-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                          <User className="h-4 w-4 text-slate-600 dark:text-slate-300" />
                        </div>
                      ) : (
                        <Avatar className={`h-8 w-8 shadow-md flex items-center justify-center bg-gradient-to-br from-purple-500 to-purple-600`}>
                          <img 
                            src="/mantisWhiteIsotype.png" 
                            alt="Mantis Logo" 
                            className="h-4 w-4 object-contain"
                          />
                        </Avatar>
                      )}
                      <div>
                        <div className={`p-3 rounded ${
                          message.role === "user" 
                            ? "bg-purple-500 text-white" 
                            : message.isError
                              ? "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800"
                              : "bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-200"
                        }`}>
                          <div className="text-sm whitespace-pre-wrap">
                            {renderFormattedContent(message.content)}
                          </div>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 ml-1">
                          {formatTime(message.timestamp)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
                
                {/* Loading indicator */}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="flex gap-3 max-w-[85%]">
                      <div className="h-8 w-8 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-sm">
                        <img 
                          src="/mantisWhiteIsotype.png" 
                          alt="Mantis Logo" 
                          className="h-4 w-4 object-contain"
                        />
                      </div>
                      <div className="p-3 rounded bg-slate-100 dark:bg-slate-700">
                        <Loader2 className="h-5 w-5 animate-spin text-slate-500 dark:text-slate-400" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
            
            {/* Input */}
            <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-transparent">
              <div className="relative">
                <div className="flex flex-col w-full relative rounded overflow-hidden border border-slate-200 dark:border-slate-700 focus-within:border-purple-500 focus-within:ring-1 focus-within:ring-purple-500 shadow-sm">
                  <div className="relative">
                    <textarea
                      ref={inputRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Pregunta sobre las estadísticas del flujo..."
                      className="w-full py-3 px-4 bg-transparent border-none focus:outline-none text-slate-800 dark:text-slate-200 resize-none overflow-hidden"
                      style={{ 
                        height: input ? 'auto' : '40px',
                        minHeight: '40px',
                        maxHeight: '150px',
                        overflowY: input.length > 80 ? 'auto' : 'hidden'
                      }}
                      rows={1}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-2 bg-transparent">
                    <div className="flex gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={onToggle}
                        className="text-xs flex items-center gap-1 text-purple-600 hover:bg-purple-500 hover:text-white dark:text-purple-400 dark:hover:bg-purple-600 dark:hover:text-white rounded transition-all duration-200 h-7 px-2 border-none shadow-none"
                      >
                        <X className="h-3 w-3" />
                        <span className="text-xs">Salir</span>
                      </Button>
                    </div>
                    
                    <Button 
                      onClick={handleSendMessage} 
                      disabled={isLoading || !input.trim()}
                      className="h-8 w-8 rounded-full flex items-center justify-center bg-purple-500 hover:bg-purple-600"
                    >
                      <Send className="h-4 w-4 text-white" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Botón circular flotante para abrir el chat */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed right-6 bottom-6 flex items-center"
          >
            <AnimatePresence>
              {isHovered && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="mr-2 bg-white dark:bg-slate-700 px-3 py-1 rounded shadow-md text-sm font-medium text-slate-800 dark:text-slate-200"
                >
                  Estadísticas
                </motion.div>
              )}
            </AnimatePresence>
            <Button
              variant="default"
              onClick={onToggle}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              className="h-14 w-14 rounded-full bg-gradient-to-r from-purple-500 to-purple-400 hover:from-purple-600 hover:to-purple-500 shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-110 p-0"
            >
              <BarChart4 className="h-6 w-6 text-white" />
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default connect(selectAuthState)(FlowCreateStats)